<script setup lang="ts">
import {
    ECOMMERCE_COLLECTIBLE_CATEGORY,
    ECOMMERCE_ITEM_BENEFIT_TYPE,
    MYSTERY_BOX_REWARD_TYPE,
    MYSTERY_BOX_TYPE,
    type MysteryBoxReward,
} from '@/types/shared/player-config'
import { capitalize, computed } from 'vue'
import FortuneWheelImage from '@/assets/images/fortune-wheel.webp'
import GoldenTicketImage from '@/assets/images/golden-ticket.png'
import TPHImage from '@/assets/images/prize-cards/php.webp'
import OfflineProfitImage from '@/assets/images/prize-cards/offline-profit2.png'
import DuckFriendsImage from '@/assets/images/gifs/duck-family.gif'
import DuckDrivingImage from '@/assets/images/gifs/duck-driving.gif'
import DuckRocketImage from '@/assets/images/gifs/duck-rocket.gif'
import DuckSkeletonImage from '@/assets/images/gifs/skeleton.gif'
import TicketImage from '@/assets/images/emojis/emoji-ticket.png'
import DefaultRewardImage from '@/assets/images/gifs/duck-money-eye.gif'
import { formatNumberFullTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import { usePlayer } from '@/composables/game/player'
import CheckIconThin from '@/components/icons/CheckIconThin.vue'

const props = defineProps<{
  reward: MysteryBoxReward
  showCheckmark?: boolean
  boxType?: MYSTERY_BOX_TYPE
}>()

const { goldenCoinValue } = usePlayer()

const skinCategoryImageMap = {
  [ECOMMERCE_COLLECTIBLE_CATEGORY.BASIC]: DuckSkeletonImage,
  [ECOMMERCE_COLLECTIBLE_CATEGORY.EPIC]: DuckDrivingImage,
  [ECOMMERCE_COLLECTIBLE_CATEGORY.PREMIUM]: DuckRocketImage,
}

const rewardItem = computed(() => props.reward)
const isCoinBenefit = computed(() => rewardItem.value.type === MYSTERY_BOX_REWARD_TYPE.ECOMMERCE_BENEFIT && (rewardItem.value.benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.GOLDEN_COIN || rewardItem.value.benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.COIN))

const configMap = computed(() => {
  if (rewardItem.value.type === MYSTERY_BOX_REWARD_TYPE.ECOMMERCE_BENEFIT) {
    if (rewardItem.value.benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.GOLDEN_TICKET) {
      return { image: GoldenTicketImage, name: 'TGE Allocation Boost' }
    }
    if (rewardItem.value.benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.SPIN) {
      return { image: FortuneWheelImage, name: `${rewardItem.value.benefit.amount} spins` }
    }
    if (rewardItem.value.benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.PROFIT_X2) {
      const isFreeBoxWithHalfHour = props.boxType === MYSTERY_BOX_TYPE.FREE && rewardItem.value.benefit.amount === 0.5
      return {
        image: TPHImage,
        name: isFreeBoxWithHalfHour
          ? `PHPx2 for 30 min`
          : `PHPx2 for ${rewardItem.value.benefit.amount} hour${rewardItem.value.benefit.amount > 1 ? 's' : ''}`,
      }
    }
    if (rewardItem.value.benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.OFFLINE_PROFIT) {
      const isFreeBoxWithHalfHour = props.boxType === MYSTERY_BOX_TYPE.FREE && rewardItem.value.benefit.amount === 0.5
      return {
        image: OfflineProfitImage,
        name: isFreeBoxWithHalfHour
          ? `+30 min offline profit`
          : `+${rewardItem.value.benefit.amount} hour${rewardItem.value.benefit.amount > 1 ? 's' : ''} offline profit`,
      }
    }
    if (rewardItem.value.benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.VIRTUAL_FRIEND) {
      return {
        image: DuckFriendsImage,
        name: `${rewardItem.value.benefit.amount} virtual friend${rewardItem.value.benefit.amount > 1 ? 's' : ''}`,
      }
    }
    if (rewardItem.value.benefit.type === ECOMMERCE_ITEM_BENEFIT_TYPE.GOLDEN_COIN) {
      const benefit = rewardItem.value.benefit.amount * goldenCoinValue.value
      return {
        image: CoinIcon,
        name: benefit > 999_999_999_999_999 ?
          formatNumberFullTruncate(benefit) :
          formatUnshortenedNumber(benefit),
      }
    }
    return {
      image: CoinIcon,
      name: rewardItem.value.benefit.amount > 999_999_999_999_999 ? formatNumberFullTruncate(rewardItem.value.benefit.amount) : formatUnshortenedNumber(rewardItem.value.benefit.amount),
    }
  }
  if (rewardItem.value.type === MYSTERY_BOX_REWARD_TYPE.SKIN) {
    const categoryImage = skinCategoryImageMap[rewardItem.value.category]
    return { image: categoryImage, name: `${capitalize(rewardItem.value.category)} skin` }
  }
  if (rewardItem.value.type === MYSTERY_BOX_REWARD_TYPE.GIVEAWAY_TICKET) {
    return {
      image: TicketImage,
      name: `${rewardItem.value.amount} Lucky Rush ticket${rewardItem.value.amount > 1 ? 's' : ''}`,
    }
  }
  return { image: DefaultRewardImage, name: '' }
})
</script>

<template>
  <div class="w-full flex items-center rounded-xl pr-3 h-[42px] overflow-hidden bg-dark-bottom-layer min-w-0">
    <div class="p-[9px] size-[42px] min-w-[42px] bg-dark-top-most-layer border-r border-r-white/[.33] flex items-center justify-center">
      <img
        class="w-6 h-auto object-cover"
        :src="configMap.image"
      >
    </div>
    <div class="pl-3 flex-1 flex min-w-0 items-center">
      <img
        v-if="isCoinBenefit"
        :src="CoinIcon"
        class="h-4 w-auto min-w-[10px] mr-1"
        alt="Coin Icon"
      >
      <span class="truncate text-base -tracking-[0.32px] font-semibold">
        <span v-if="rewardItem.multiplier">{{ rewardItem.multiplier }} x </span>{{ configMap.name }}
      </span>
    </div>
    <CheckIconThin
      v-if="showCheckmark"
      class="ml-auto size-6 min-w-[24px] text-teal-primary"
    />
  </div>
</template>

<style scoped>

</style>